import type { CampaignDraftDefinition, CampaignMapDraft, CharacterDefinition, CharacterGender, CoreStats, CreationVisibility } from "../types";

export type CharacterEditorStepId = "identity" | "personality" | "statistics" | "public" | "review";

const NPC_CHARACTER_STEPS: readonly CharacterEditorStepId[] = ["identity", "personality", "statistics", "public", "review"];
const PLAYER_CHARACTER_STEPS: readonly CharacterEditorStepId[] = ["identity", "statistics", "public", "review"];

export function characterEditorStepIds(role: CharacterDefinition["role"]): readonly CharacterEditorStepId[] {
  return role === "player" ? PLAYER_CHARACTER_STEPS : NPC_CHARACTER_STEPS;
}

export function visibleCharacterEditorStep(role: CharacterDefinition["role"], requested: CharacterEditorStepId): CharacterEditorStepId {
  return role === "player" && requested === "personality" ? "statistics" : requested;
}

export const DEFAULT_STATS: CoreStats = { STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10, FEM: 0, COR: 0 };

export const TRANSFORMATION_THEMES = [
  ["male_to_female", "Male to Female"], ["female_to_male", "Female to Male"],
  ["sissification", "Sissification"], ["bimbofication", "Bimbofication"],
  ["body_swap", "Body swap"], ["age_regression", "Adult age regression"],
  ["shrinking", "Shrinking"], ["animal", "Animal transformation"],
  ["furry_anthropomorphic", "Furry / anthropomorphic"], ["demon", "Demon"],
  ["robot", "Robot"], ["inanimate_object", "Inanimate object"],
  ["mental_changes", "Mental changes"], ["voluntary", "Voluntary"],
  ["involuntary", "Involuntary"],
] as const;

export const ADULT_THEMES = [
  ["hetero_sex", "Hetero sex"], ["lesbian_sex", "Lesbian sex"],
  ["gay_sex", "Gay sex"], ["group_sex", "Group sex"],
  ["submission", "Submission"], ["domination", "Domination"],
  ["pregnancy", "Pregnancy"], ["consensual_non_consent", "Consensual non-consent"],
] as const;

export const VISIBILITY_COPY: Record<CreationVisibility, { label: string; detail: string }> = {
  private: { label: "Private", detail: "Only you can find or open it." },
  unlisted: { label: "Unlisted", detail: "Anyone signed in with the private link can open it." },
  public: { label: "Public", detail: "Visible in the searchable community library." },
};

export function emptyCharacterDefinition(): CharacterDefinition {
  return {
    name: "Untitled character",
    gender: "nonbinary",
    adultAge: 25,
    pronouns: "they/them",
    role: "npc",
    appearance: "",
    personality: "",
    personalityTraits: [],
    background: "",
    speechStyle: "",
    motivations: "",
    likes: [],
    dislikes: [],
    privateNotes: "",
    aiInstructions: "",
    boundaries: ["Adult characters only"],
    stats: { ...DEFAULT_STATS },
    profileMediaId: null,
    publicDescription: "",
    tags: [],
  };
}

export function emptyCampaignDefinition(): CampaignDraftDefinition {
  return {
    basics: {
      title: "Untitled campaign",
      tone: "",
      genre: "",
      storyOutline: "",
      openingScene: "",
      pacing: "moderate",
      targetTurns: 60,
    },
    themes: { transformation: [], adult: [] },
    agency: { mode: "mechanical", femStart: 0, corStart: 0, customMeters: [] },
    transformations: [],
    sexScenes: [],
    characters: [],
    world: { description: "", maps: [] },
    chapters: [],
    startingChapterId: null,
    mechanics: { mapMovement: true, tabletopBasics: true, diceRolls: true },
    publicInfo: { description: "", tags: [], bannerMediaId: null },
  };
}

export function creationShareUrl(kind: "character" | "campaign", id: string, token: string | null | undefined): string | null {
  if (!token) return null;
  return `${window.location.origin}/app/shared/${kind}/${encodeURIComponent(id)}?share=${encodeURIComponent(token)}`;
}

export function shortDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Just now" : new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

export function normalizeAdultAgeInput(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 18;
  return Math.max(18, Math.trunc(parsed));
}

export function defaultPronounsForGender(gender: CharacterGender): string {
  if (gender === "cis_woman" || gender === "trans_woman") return "she/her";
  if (gender === "cis_man" || gender === "trans_man") return "he/him";
  return "they/them";
}

export function characterReviewMissingFields(definition: CharacterDefinition): string[] {
  return [
    !definition.name.trim() && "Name",
    definition.adultAge < 18 && "Adult age",
    !definition.appearance.trim() && "Appearance",
    definition.role === "npc" && !definition.personality.trim() && "Personality",
  ].filter((value): value is string => typeof value === "string");
}

export function characterPortraitPrompt(character: Pick<CharacterDefinition, "adultAge" | "gender">): string {
  const identity = character.gender === "cis_woman" || character.gender === "trans_woman"
    ? "woman"
    : character.gender === "cis_man" || character.gender === "trans_man"
      ? "man"
      : character.gender === "nonbinary"
        ? "nonbinary person"
        : "person whose gender presentation follows the creator's description";
  if (character.adultAge > 120) {
    return [
      `Internal character portrait rules. Depict a youthful, ageless adult ${identity}. The character's exceptional chronological age is lore metadata, not an apparent-age instruction, and must not make them look elderly. Follow any explicit adult apparent-age cues in the creator's appearance description; when none are present, default to a glamorous adult in their twenties.`,
      "Use youthful adult facial proportions, smooth firm flawless skin, full youthful cheeks, bright clear eyes, and a fresh energetic expression. Keep every identifying appearance detail requested by the creator while never translating a supernatural or exceptionally long lifespan into visible aging.",
      "Make the character strikingly beautiful, sexy, sensual, and confidently provocative, with polished high-glamour styling, an alluring expression, and a tastefully raunchy NSFW atmosphere. Center erotic appeal, bold styling, and premium fantasy polish.",
      "Single-character profile portrait, attractive face, consistent anatomy, no text, no watermark.",
    ].join("\n\n");
  }
  const visualAge = Math.max(18, character.adultAge - 10);
  const ageArticle = visualAge === 18 || (visualAge >= 80 && visualAge < 90) ? "an" : "a";
  return [
    `Internal character portrait rules. Visual-age priority: depict exactly ${ageArticle} ${visualAge}-year-old adult ${identity}. This is the sole apparent-age instruction and the subject is an adult. Treat any other age wording in the creator description as nonvisual metadata.`,
    "Use facial proportions appropriate to that precise youthful adult age: smooth firm flawless skin, full youthful cheeks, bright clear eyes, and a fresh energetic expression. Keep every identifying appearance detail requested by the creator while applying the mandated apparent age.",
    "Make the character strikingly beautiful, sexy, sensual, and confidently provocative, with polished high-glamour styling, an alluring expression, and a tastefully raunchy NSFW atmosphere. Center erotic appeal, bold styling, and premium fantasy polish.",
    "Single-character profile portrait, attractive face, consistent anatomy, no text, no watermark.",
  ].filter(Boolean).join("\n\n");
}

export function mapMoodArtPrompt(): string {
  return [
    "Internal map mood art rules. Create an environment-only establishing shot in which the location itself is the sole subject.",
    "The space must be completely unoccupied. Absolutely no people, characters, humans, humanoids, women, men, girls, boys, figures, creatures, animals, faces, bodies, limbs, silhouettes, crowds, or living beings. Do not include figures in shadows, reflections, windows, mirrors, paintings, screens, or background details. Do not add statues, mannequins, dolls, or person-shaped forms.",
    "Express every glamorous, sensual, provocative, or adult mood exclusively through architecture, decor, furniture, materials, lighting, color, atmosphere, and inanimate props—not through a person or character. Compose a wide location view with no foreground subject, ready for characters to enter later, but with nobody currently present.",
  ].join("\n\n");
}

export function withGeneratedTileMap(map: CampaignMapDraft, value: unknown): CampaignMapDraft {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return map;
  const tileMap = value as Record<string, unknown>;
  const dimension = (candidate: unknown, fallback: number): number => typeof candidate === "number" && Number.isFinite(candidate)
    ? Math.max(6, Math.min(50, Math.trunc(candidate)))
    : fallback;
  return {
    ...map,
    width: dimension(tileMap.width, map.width),
    height: dimension(tileMap.height, map.height),
    tileMap,
  };
}
